// =====================================================================
// src/pages/intl-acca/IntlAccaHomePage.tsx
// =====================================================================
// Live feed surface for an ACTIVE tournament.
// Presentational only — receives a `data` prop. The host wires the
// real Supabase calls (see the data-binding contract at the bottom of
// this file).
//
// Routes mounted at: /intl-acca/:tournamentId (nested per G3 ruling).

import type {
  IntlAccaTournament,
  IntlAccaSlipWithLegs,
  IntlAccaPassPurchase,
} from "../../db/intl-acca-types";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { IntlAccaSlipCard } from "../../components/intl-acca/IntlAccaSlipCard";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { PassPurchaseCard } from "../../components/intl-acca/PassPurchaseCard";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";

export interface IntlAccaHomePageProps {
  /** Loading flag — host renders skeleton when true (component does NOT). */
  loading: boolean;
  /** Error string if the tournament fetch failed. */
  error: string | null;
  /** The active tournament context. */
  tournament: IntlAccaTournament | null;
  /** Today's published slips (FOCUS first, then VALUE, then POWER). */
  slips: IntlAccaSlipWithLegs[];
  /** Existing pass for the current user, if any. */
  existingPass: IntlAccaPassPurchase | null;
  /** match_id → display label, resolved by the host. */
  matchLabels?: Record<string, string>;
  /** Tier gates — host computes with `tierMeets(currentTier, 'sika_pro' / 'pro' …)`. */
  tier: {
    canSeeFocus: boolean;
    canSeeValue: boolean;
    canSeePower: boolean;
  };
  /** True when the viewer's tier auto-includes the Tournament Pass
   *  (Sika Pro+ / founder). Suppresses the pass paywall + price CTA. */
  complimentaryPass?: boolean;
  /** Wiring callbacks. */
  onUpgradeFocus?: () => void;
  onUpgradeValue?: () => void;
  onUpgradePower?: () => void;
  onSharePass?: () => void;
  onPurchasePass?: () => void;
  onShareSlip?: (slipId: string) => void;
}

export function IntlAccaHomePage(props: IntlAccaHomePageProps): JSX.Element {
  const {
    loading, error, tournament, slips, existingPass, matchLabels,
    tier, complimentaryPass, onUpgradeFocus, onUpgradeValue, onUpgradePower,
    onPurchasePass, onShareSlip,
  } = props;

  const focus = slips.find((s) => s.slip_type === "FOCUS");
  const value = slips.find((s) => s.slip_type === "VALUE");
  const power = slips.find((s) => s.slip_type === "POWER");

  const hasAnySlip = !!(focus || value || power);
  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: hasAnySlip });

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading tournament…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Could not load this tournament."
          body={error}
        />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Tournament not found."
          body="No row in intl_acca_tournaments matched the requested id."
        />
      </div>
    );
  }

  return (
    <div className="ia-root">
      <TournamentStateBanner tournament={tournament} />

      <div className="ia-page-pad" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 32 }}>
        {/* MAIN COLUMN — slips */}
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {/* FOCUS */}
          <Section title="Focus Acca · Today">
            {!focus ? (
              <HonestEmptyState variant="no-signals" />
            ) : tier.canSeeFocus ? (
              <IntlAccaSlipCard
                slip={focus}
                matchLabels={matchLabels}
                onShare={() => onShareSlip?.(focus.id)}
              />
            ) : (
              <TierLockedTeaser gate="focus_acca" onUpgrade={onUpgradeFocus}>
                <IntlAccaSlipCard slip={focus} matchLabels={matchLabels} />
              </TierLockedTeaser>
            )}
          </Section>

          {/* VALUE */}
          <Section title="Value Acca · Today">
            {!value ? (
              <HonestEmptyState variant="no-signals" />
            ) : tier.canSeeValue ? (
              <IntlAccaSlipCard
                slip={value}
                matchLabels={matchLabels}
                onShare={() => onShareSlip?.(value.id)}
              />
            ) : (
              <TierLockedTeaser gate="value_acca" onUpgrade={onUpgradeValue}>
                <IntlAccaSlipCard slip={value} matchLabels={matchLabels} />
              </TierLockedTeaser>
            )}
          </Section>

          {/* POWER */}
          {power ? (
            <Section title="Power Acca · Today">
              {tier.canSeePower ? (
                <IntlAccaSlipCard
                  slip={power}
                  matchLabels={matchLabels}
                  onShare={() => onShareSlip?.(power.id)}
                />
              ) : (
                <TierLockedTeaser gate="power_acca" onUpgrade={onUpgradePower}>
                  <IntlAccaSlipCard slip={power} matchLabels={matchLabels} />
                </TierLockedTeaser>
              )}
            </Section>
          ) : null}

          {/* Doctrine fallback when no slips exist */}
          {!hasAnySlip && (
            <DoctrineSection
              probabilityPicks={doctrineProbPicks}
              valuePicks={doctrineValuePicks}
              isFullFallback
              substrateEmpty={substrateEmpty}
              substrateWeek={substrateWeek}
            />
          )}
        </div>

        {/* RAIL — pass + future widgets */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <PassPurchaseCard
            tournament={tournament}
            existingPass={existingPass}
            complimentaryViaSubscription={complimentaryPass}
            onPurchase={onPurchasePass}
            primary
          />
        </aside>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section>
      <div className="ia-eyebrow" style={{ marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

// =====================================================================
// DATA BINDING CONTRACT — for the host wiring this page
// =====================================================================
//
// Host code that mounts this page must do the following async fetches
// in parallel and pass the results in as props. No fetching happens
// inside this component.
//
// const { data: tournament }   = await supabase.from('intl_acca_tournaments')
//                                     .select('*').eq('id', tournamentId).single();
//
// const { data: slips }        = await supabase.from('intl_acca_slips')
//                                     .select('*').eq('tournament_id', tournamentId)
//                                     .eq('status', 'ACTIVE')
//                                     .order('generated_at', { ascending: false });
//
// const { data: legs }         = await supabase.from('intl_acca_slip_legs')
//                                     .select('*').in('slip_id', slipIds);
//
// const { data: existingPass } = await supabase.from('intl_acca_pass_purchases')
//                                     .select('*').eq('user_id', userId)
//                                     .eq('tournament_id', tournamentId).maybeSingle();
//
// Then merge legs into slips by slip_id, and pass the resulting
// IntlAccaSlipWithLegs[] in. matchLabels is host-resolved (a future
// C-phase Match entity will let this become a single join — for now,
// resolve match_id → label via your fixture cache).
